
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shirt, User, UserRound, CheckCircle, Package } from 'lucide-react';
import { TShirtStats } from '@/hooks/admin/useTShirtStats';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';

interface TShirtSizeSummaryProps {
  stats: TShirtStats;
  className?: string;
}

// Helper to sort sizes in the correct order (S, M, L, XL, 2XL, etc.)
const sortSizes = (sizes: Record<string, number>): [string, number][] => {
  const sizeOrder: { [key: string]: number } = {
    'S': 1,
    'M': 2,
    'L': 3,
    'XL': 4,
    '2XL': 5,
    '3XL': 6,
    '4XL': 7,
    '5XL': 8
  };

  return Object.entries(sizes).sort((a, b) => {
    const orderA = sizeOrder[a[0]] || 999;
    const orderB = sizeOrder[b[0]] || 999;
    return orderA - orderB;
  });
};

const TShirtSizeSummary: React.FC<TShirtSizeSummaryProps> = ({ stats, className }) => {
  const menSizes = sortSizes(stats.menSizes);
  const womenSizes = sortSizes(stats.womenSizes);
  
  const distributionProgress = stats.totalSized > 0 
    ? (stats.totalDistributed / stats.totalSized) * 100 
    : 0;

  return (
    <Card className={`${className || ''} w-full`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Shirt className="h-4 w-4" />
          Chi tiết áo Kỷ Niệm
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        {/* Distribution progress */}
        <div className="space-y-1 mb-3">
          <div className="flex items-center justify-between text-xs">
            <motion.span 
              className="flex items-center"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1 text-green-500" />
              <span>{stats.totalDistributed} / {stats.totalSized} áo đã phát ({Math.round(distributionProgress)}%)</span>
            </motion.span>
            {stats.totalRemaining > 0 && (
              <motion.span 
                className="flex items-center text-blue-600"
                initial={{ opacity: 0, x: 5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Package className="h-3.5 w-3.5 mr-1" />
                {stats.totalRemaining} áo còn lại
              </motion.span>
            )}
          </div>
          <Progress value={distributionProgress} className="h-2" />
        </div>
        
        {/* Size distribution tables */}
        <div className="grid grid-cols-2 gap-4">
          {/* Men's sizes */}
          <div>
            <h4 className="text-xs font-medium flex items-center gap-1 mb-1 text-blue-700">
              <User className="h-3.5 w-3.5" />
              Nam ({stats.totalMen})
            </h4>
            <motion.div 
              className="space-y-1"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {menSizes.length === 0 ? (
                <span className="text-xs text-muted-foreground italic">Không có dữ liệu</span>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-b border-gray-100">
                      <th className="text-left py-1 font-medium">Size</th>
                      <th className="text-center py-1 font-medium">SL</th>
                      <th className="text-right py-1 font-medium">Đã phát</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menSizes.map(([size, count], index) => {
                      const distributed = stats.distributedMenSizes[size] || 0;
                      return (
                        <motion.tr 
                          key={`men-${size}`}
                          initial={{ opacity: 0, y: 3 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: 0.05 * index }}
                          className={distributed === count ? "bg-green-50 dark:bg-green-900/10" : ""}
                        >
                          <td className="py-0.5 font-medium">{size}</td>
                          <td className="py-0.5 text-center">{count}</td>
                          <td className="py-0.5 text-right text-green-600">{distributed}</td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </motion.div>
          </div>
          
          {/* Women's sizes */}
          <div>
            <h4 className="text-xs font-medium flex items-center gap-1 mb-1 text-pink-700">
              <UserRound className="h-3.5 w-3.5" />
              Nữ ({stats.totalWomen})
            </h4>
            <motion.div 
              className="space-y-1"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {womenSizes.length === 0 ? (
                <span className="text-xs text-muted-foreground italic">Không có dữ liệu</span>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-b border-gray-100">
                      <th className="text-left py-1 font-medium">Size</th>
                      <th className="text-center py-1 font-medium">SL</th>
                      <th className="text-right py-1 font-medium">Đã phát</th>
                    </tr>
                  </thead>
                  <tbody>
                    {womenSizes.map(([size, count], index) => {
                      const distributed = stats.distributedWomenSizes[size] || 0;
                      return (
                        <motion.tr 
                          key={`women-${size}`}
                          initial={{ opacity: 0, y: 3 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: 0.05 * index }}
                          className={distributed === count ? "bg-green-50 dark:bg-green-900/10" : ""}
                        >
                          <td className="py-0.5 font-medium">{size}</td>
                          <td className="py-0.5 text-center">{count}</td>
                          <td className="py-0.5 text-right text-green-600">{distributed}</td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </motion.div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TShirtSizeSummary;
